// src/utils/intakeLead.js
// Pure helpers for the Listing/Buyer Request intake -> pipeline lead handoff.
// No React, no Firestore: the form resolves its contacts first and passes them
// in, so both helpers stay unit-testable.

// Same display-name precedence the intake form already uses for the primary
// client (main-list-request.jsx): name -> clientName -> "first last" join,
// with a bare lastname fallback.
const displayName = (contact) => {
  const c = contact || {};
  const full = String(
    c.name ||
      c.clientName ||
      [c.firstname, c.lastname].filter(Boolean).join(" ") ||
      "",
  ).trim();
  const last = String(c.lastname || "").trim();
  return full || last || "";
};

/**
 * Build the pipeline-lead payload for an intake submission.
 *
 * @param {Object} args
 * @param {string} args.type          "seller" (or "listing") for a listing lead; anything else = buyer.
 * @param {string[]} args.clientIds   Form client ids in order (blanks skipped); first = primary.
 * @param {Object} args.contactsById  Map of contactId -> contact object already loaded by the form
 *                                    (the form fetches any missing ones via getContactsByIds first;
 *                                    an id with no entry falls back to a blank name).
 * @param {string|number} args.dealId Id of the hidden deal stub (becomes sourceDealId).
 * @param {Object} args.agent         { id: chosen agent id, name: chosen agent name, enteredById: logged-in user id }.
 * @param {Object} args.leadSourceObj The picked SYSTEM lead-source object (deals store the full
 *                                    object; leads store just its id).
 * @param {string} args.listingDate   Listing appointment date (YYYY-MM-DD input value); listing only.
 * @param {string} args.notes         Form notes.
 * @returns the lead payload for saveListingLead / saveBuyerLead. The seller
 *          call site still spreads in propertyAddress, exactly as before.
 */
export function buildIntakeLead({
  type,
  clientIds,
  contactsById,
  dealId,
  agent,
  leadSourceObj,
  listingDate,
  notes,
} = {}) {
  const isListing = type === "seller" || type === "listing";
  const ids = (Array.isArray(clientIds) ? clientIds : []).filter(Boolean);
  const contacts = contactsById || {};
  const primaryId = ids[0] || "";
  const { id: agentId = "", name: agentName = "", enteredById = "" } =
    agent || {};
  const sourceId =
    (leadSourceObj && (leadSourceObj.id || leadSourceObj.docId)) || "";

  const lead = {
    // --- Existing leadBase fields, unchanged ---
    contactId: primaryId,
    contactName: primaryId ? displayName(contacts[primaryId]) : "",
    assignedAgent: agentId,
    assignedAgentName: agentName,
    createdby: agentId || enteredById,
    enteredBy: enteredById,
    stage: "New Lead",
    notes: notes || "",
    sourceDealId: String(dealId ?? ""),
    // --- New: the partner(s) ride along as co-clients (same stored shape
    //     listClients/the drawers read: {contactId, contactName}) ---
    coClients: ids.slice(1).map((contactId) => ({
      contactId,
      contactName: displayName(contacts[contactId]),
    })),
  };

  // New: the lead source's system id — listing leads store it in `source`,
  // buyer leads in `leadSource` (leadSourceLabel resolves either).
  if (isListing) {
    lead.source = sourceId;
    // New: the listing appointment date, so the pipeline card shows its badge.
    lead.appointmentAt = listingDate || "";
  } else {
    lead.leadSource = sourceId;
  }

  return lead;
}

/**
 * Deduped union of the intake stub's customers and the lead's clients, used at
 * signing so restoring the stub NEVER deletes anyone:
 * lead primary first, then the lead's other clients, then any stub-only ids.
 */
export function mergeClientIds(stubCustomers, leadClientIds) {
  const out = [];
  const seen = new Set();
  const push = (id) => {
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  };
  (Array.isArray(leadClientIds) ? leadClientIds : []).forEach(push);
  (Array.isArray(stubCustomers) ? stubCustomers : []).forEach(push);
  return out;
}
